import { forwardRef, useId, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/cn';
import { controlClassName, controlTone, describedBy, FieldWrapper } from './field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  containerClassName?: string;
  /** Where the popup opens. Defaults to 'item-aligned' like a native select. */
  position?: 'item-aligned' | 'popper';
  'aria-label'?: string;
}

/**
 * Radix Select rather than a native `<select>`: the option list has to be
 * styleable in dark mode and long lists (every IANA timezone) need a scroll
 * affordance a native control does not give us on every platform.
 *
 * Radix handles the keyboard model — type-ahead, arrows, Home/End, Escape —
 * which is exactly the part a hand-rolled listbox always gets wrong.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    id,
    label,
    error,
    hint,
    value,
    onValueChange,
    options,
    placeholder = 'Select…',
    disabled,
    required,
    name,
    className,
    containerClassName,
    position = 'item-aligned',
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const hasError = Boolean(error);

  return (
    <FieldWrapper
      id={selectId}
      label={label}
      error={error}
      hint={hint}
      required={required}
      className={containerClassName}
    >
      <SelectPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        name={name}
        required={required}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={selectId}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy(selectId, { hint, error })}
          className={cn(
            controlClassName,
            controlTone(hasError),
            'flex h-11 items-center justify-between gap-2 px-3 text-left text-base sm:text-sm',
            'data-[placeholder]:text-muted',
            className,
          )}
          {...rest}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position={position}
            sideOffset={position === 'popper' ? 6 : undefined}
            className={cn(
              'z-50 overflow-hidden rounded-lg border border-line bg-surface text-ink shadow-pop',
              'max-h-[min(24rem,var(--radix-select-content-available-height))]',
              position === 'popper'
                ? 'w-[var(--radix-select-trigger-width)]'
                : 'min-w-[var(--radix-select-trigger-width)]',
            )}
          >
            <SelectPrimitive.ScrollUpButton className="flex h-7 items-center justify-center bg-surface text-muted">
              <ChevronUp className="size-4" aria-hidden="true" />
            </SelectPrimitive.ScrollUpButton>

            <SelectPrimitive.Viewport className="p-1">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    'relative flex min-h-11 cursor-pointer select-none items-center',
                    'rounded-md py-2 pl-8 pr-3 text-sm outline-none',
                    'data-[highlighted]:bg-raised data-[highlighted]:text-ink',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  )}
                >
                  <span className="absolute left-2 flex size-4 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="size-4 text-accent" aria-hidden="true" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>

            <SelectPrimitive.ScrollDownButton className="flex h-7 items-center justify-center bg-surface text-muted">
              <ChevronDown className="size-4" aria-hidden="true" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </FieldWrapper>
  );
});
